import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ErpConnectionView, ErpMappingRow, ErpMappingState } from '../../api';
import { erpApi } from '../../api';
import { statusFor } from '../../labels';
import { useResource } from '../../hooks';
import { Callout, FilterChips, HelpTip, Input, Pager, Select } from '../primitives';
import { QuietEmpty } from '../project/ui';
import { ErrorNotice, LoadingRows } from '../ui';
import { storeCopyFor, useStoreCopy } from './storeCopy';
import { formatNumber, linkedWebsites, severalStores, websiteLabel } from './labels';
import { ExportMatchesDialog } from './matching/ExportMatchesDialog';
import { ImportMatchesDialog } from './matching/ImportMatchesDialog';
import { MatchQueue } from './matching/MatchQueue';
import { MatchWorkspace } from './matching/MatchWorkspace';
import { RecentMatches } from './matching/RecentMatches';
import { ReviewMatchesDialog } from './matching/ReviewMatchesDialog';
import { cannotSaveText, matchFilterOrder } from './matching/matchingCopy';
import { undoablePhases, useRecentMatches } from './matching/useRecentMatches';

const PAGE_SIZE = 50;
/** Below this width of the workspace the flow is sequential: the queue, then one product. */
const WIDE = 900;
/** How often the list is read again while the store has not answered a request. */
const PENDING_POLL_MS = 4000;

type Filter = ErpMappingState | '';

/**
 * What of the panel the page's address keeps (CO-164): the website, the
 * search, the group of products and the open product. A value the address does
 * not have means the default. `state` is a match state or "all".
 */
export type MatchingView = { store?: string; q?: string; state?: string; sku?: string };
export type MatchingViewPatch = { [K in keyof MatchingView]?: string | undefined };
/**
 * How the address changes: a product chosen on its own entry (so Back returns
 * to the list), everything else by replacing the entry, and "back" for leaving
 * the open product the way the browser's Back does.
 */
export type MatchingViewChange = (patch: MatchingViewPatch, mode: 'push' | 'replace' | 'back') => void;

const FILTER_OF_STATE: Record<string, Filter> = { all: '', no_code: 'no_code', unmatched: 'unmatched', conflict: 'conflict', matched: 'matched' };

function applyViewPatch(view: MatchingView, patch: MatchingViewPatch): MatchingView {
  const next: MatchingView = { ...view };
  for (const key of Object.keys(patch) as (keyof MatchingView)[]) {
    const value = patch[key];
    if (value === undefined || value === '') delete next[key];
    else next[key] = value;
  }
  return next;
}

/**
 * Whether an element is narrower than a width, as it is resized (container
 * queries cannot tell script). The element is state, not a ref: it only exists
 * once the products have loaded, and it is replaced when the view changes.
 */
function useNarrow(element: HTMLElement | null, width: number): boolean {
  const [narrow, setNarrow] = useState(false);
  useEffect(() => {
    if (!element || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(([entry]) => {
      if (entry) setNarrow(entry.contentRect.width < width);
    });
    observer.observe(element);
    setNarrow(element.getBoundingClientRect().width < width);
    return () => observer.disconnect();
  }, [element, width]);
  return narrow;
}

/**
 * Product matching: which ERP article each store product takes its stock,
 * price and product data from, and a workspace for finding the right one for
 * the products that have none (CO-155, CO-170). A product matches by the
 * store's own matching attribute and nothing else (CO-79); the workspace puts
 * the queue, the store's product and the ERP's candidates side by side, so a
 * person can match, skip, step on and undo without opening and closing
 * anything. Each website of a store has its own attribute and products
 * (CO-134).
 */
export function ErpMappingPanel({
  connection,
  canManage = false,
  initialQuery = '',
  initialStoreId,
  view: addressView,
  onViewChange,
  onOpenStores,
}: {
  connection: ErpConnectionView;
  /** The member may manage ERP integrations: only then is saving a match offered. */
  canManage?: boolean;
  initialQuery?: string;
  /** A linked website's key (websiteKey), or a store id for its first linked website. */
  initialStoreId?: string;
  /** Where the page's address keeps the view (CO-164). Without it the panel keeps the view itself, starting from the two above. */
  view?: MatchingView;
  onViewChange?: MatchingViewChange;
  /** Where a store that cannot save matches is updated: the Stores tab. */
  onOpenStores?: () => void;
}) {
  const linked = useMemo(() => linkedWebsites(connection), [connection]);
  const projectWords = useStoreCopy();

  // The view is the address's when the page gives one, and the panel's own
  // otherwise (a tab that is not routed, a test).
  const controlled = onViewChange !== undefined;
  const [ownView, setOwnView] = useState<MatchingView>(() => applyViewPatch({}, { store: initialStoreId, q: initialQuery }));
  const view = controlled ? (addressView ?? {}) : ownView;
  const onViewChangeRef = useRef(onViewChange);
  onViewChangeRef.current = onViewChange;
  const change = useCallback<MatchingViewChange>((patch, mode) => {
    if (onViewChangeRef.current) onViewChangeRef.current(patch, mode);
    else setOwnView((current) => applyViewPatch(current, patch));
  }, []);

  const target =
    linked.find((entry) => entry.key === view.store) ?? linked.find((entry) => entry.store.id === view.store) ?? linked[0];
  const targetKey = target?.key ?? '';
  // Opened plainly, the list is what needs a person; a link to a product or
  // a search (an issue's product link, UX-19) opens it across every state.
  const [nothingToMatch, setNothingToMatch] = useState('');
  const explicit = view.state !== undefined || Boolean(view.q) || Boolean(view.sku);
  const filter: Filter =
    view.state !== undefined && view.state in FILTER_OF_STATE
      ? FILTER_OF_STATE[view.state]!
      : explicit || nothingToMatch === targetKey
        ? ''
        : 'no_code';
  const chosenFilter = explicit;
  const query = view.q ?? '';
  const skuParam = view.sku;
  // What is typed is shown at once; the address follows after a pause, so
  // Back does not step through keystrokes.
  const [search, setSearch] = useState(query);
  const [offset, setOffset] = useState(0);
  const [dialog, setDialog] = useState<'review' | 'import' | 'export' | null>(null);
  // Products set aside in this sitting: they stay in the queue, dimmed, and are
  // only come back to when nothing else is left.
  const [skipped, setSkipped] = useState<ReadonlySet<string>>(new Set());
  const [finished, setFinished] = useState(false);
  const deskHeading = useRef<HTMLHeadingElement>(null);
  const [workEl, setWorkEl] = useState<HTMLDivElement | null>(null);
  const narrow = useNarrow(workEl, WIDE);
  // The product was opened on an entry of its own, so leaving it can be Back.
  const deskPushed = useRef(false);
  // Where focus goes once the address has changed the view: the address commits
  // after the click, so the desk (or the list) is only there to focus then.
  const focusAfter = useRef<{ kind: 'desk' } | { kind: 'row'; sku: string } | null>(null);
  // After a page change, which product to open once the page has loaded.
  const pendingSelect = useRef<'first' | 'last' | null>(null);

  useEffect(() => {
    const typed = search.trim();
    if (typed === query) return undefined;
    const handle = window.setTimeout(() => {
      // The group stays what it is: a search never changes it.
      change({ q: typed, state: filter === '' ? 'all' : filter, sku: undefined }, 'replace');
    }, 250);
    return () => window.clearTimeout(handle);
  }, [search, query, filter, change]);
  // The address changed by itself (a link, Back): the box follows it.
  useEffect(() => {
    setSearch((typed) => (typed.trim() === query ? typed : query));
  }, [query]);
  useEffect(() => {
    setOffset(0);
    setFinished(false);
  }, [targetKey, filter, query]);
  useEffect(() => setSkipped(new Set()), [targetKey]);

  const mappings = useResource(
    () =>
      target
        ? erpApi.mappings(connection.id, {
            installation_id: target.store.id,
            website_code: target.website.code,
            state: filter,
            q: query,
            limit: PAGE_SIZE,
            offset,
          })
        : Promise.resolve(null),
    [connection.id, targetKey, filter, query, offset, connection.last_stock_sync_success_at],
    `${connection.id}:${targetKey}`,
    { key: `erp:${connection.id}:mappings:${targetKey}:${filter}:${query}:${offset}` },
  );

  const rows = useMemo<ErpMappingRow[]>(() => mappings.data?.items ?? [], [mappings.data]);
  const counts = mappings.data?.counts ?? {};

  // A request to save a match is answered by the store within a minute or so:
  // while one is open, the list reads itself again (the store is the one that
  // knows).
  const anyPending = rows.some((row) => row.pending?.state === 'pending');
  const reload = mappings.reload;
  // The latest reload, so the timer does not restart whenever the page renders.
  const reloadRef = useRef(reload);
  reloadRef.current = reload;
  useEffect(() => {
    if (!anyPending) return undefined;
    const handle = window.setInterval(() => reloadRef.current(), PENDING_POLL_MS);
    return () => window.clearInterval(handle);
  }, [anyPending]);

  const recent = useRecentMatches(connection.id, reload);

  // When nothing needs a match, show everything instead of an empty list the
  // person did not ask for.
  useEffect(() => {
    if (!chosenFilter && filter === 'no_code' && mappings.data && (counts.no_code ?? 0) === 0) setNothingToMatch(targetKey);
  }, [chosenFilter, filter, mappings.data, counts.no_code, targetKey]);

  // The open product is the address's, else the first of the list; one the list
  // does not have is said, and the first is shown instead.
  const selected = rows.find((row) => row.sku === skuParam) ?? rows[0];
  const selectedSku = selected?.sku;
  const selectedIndex = selected ? rows.findIndex((row) => row.sku === selected.sku) : -1;
  const deskOpen = Boolean(skuParam) && selected?.sku === skuParam;
  useEffect(() => {
    const want = focusAfter.current;
    if (!want) return;
    if (want.kind === 'desk' && deskOpen) {
      focusAfter.current = null;
      deskHeading.current?.focus();
    } else if (want.kind === 'row' && !deskOpen) {
      focusAfter.current = null;
      document.querySelector<HTMLElement>(`[data-sku="${CSS.escape(want.sku)}"]`)?.focus();
    }
  }, [deskOpen, rows, selectedSku]);
  // In the sequential flow a product opens at its top, with the queue's controls out of the way.
  useEffect(() => {
    if (narrow && deskOpen && typeof workEl?.scrollIntoView === 'function') workEl.scrollIntoView({ block: 'start' });
  }, [narrow, deskOpen, selectedSku, workEl]);
  const skuMissing = Boolean(skuParam) && mappings.data !== undefined && mappings.data !== null && !mappings.loading && selected?.sku !== skuParam;

  const total = mappings.data?.total ?? 0;
  const needsFilter = filter === 'no_code' || filter === 'unmatched' || filter === '';
  const stateGroup = filter === '' ? 'all' : filter;

  // ---- moving through the queue -------------------------------------------
  const open = useCallback(
    (sku: string, mode: 'push' | 'replace') => {
      change({ sku, state: stateGroup }, mode);
      focusAfter.current = { kind: 'desk' };
    },
    [change, stateGroup],
  );
  // A product that still needs a person: not matched yet, not asked of the
  // store, not set aside. Browsing other groups visits every product.
  const eligible = useCallback(
    (row: ErpMappingRow) => {
      if (skipped.has(row.sku) || row.pending?.state === 'pending') return false;
      return needsFilter ? row.state === 'no_code' || row.state === 'unmatched' : true;
    },
    [skipped, needsFilter],
  );
  const advance = useCallback(
    (from: string, setAside: boolean) => {
      const at = rows.findIndex((row) => row.sku === from);
      const rest = rows.slice(at + 1);
      const next = rest.find((row) => eligible(row) && row.sku !== from);
      if (next) return open(next.sku, 'replace');
      if (offset + rows.length < total) {
        pendingSelect.current = 'first';
        setOffset(offset + PAGE_SIZE);
        return undefined;
      }
      const before = rows.slice(0, Math.max(at, 0)).find((row) => eligible(row) && row.sku !== from);
      if (before) return open(before.sku, 'replace');
      // Only products that were set aside are left: come back to them.
      const aside = rows.find((row) => row.sku !== from && skipped.has(row.sku) && row.pending?.state !== 'pending' && (row.state === 'no_code' || row.state === 'unmatched'));
      if (aside && !setAside) {
        setSkipped((current) => {
          const out = new Set(current);
          out.delete(aside.sku);
          return out;
        });
        return open(aside.sku, 'replace');
      }
      setFinished(true);
      return undefined;
    },
    [rows, eligible, offset, total, open, skipped],
  );
  const step = useCallback(
    (direction: -1 | 1) => {
      if (selectedIndex < 0) return;
      const index = selectedIndex + direction;
      if (index >= 0 && index < rows.length) return open(rows[index]!.sku, 'replace');
      if (direction === 1 && offset + rows.length < total) {
        pendingSelect.current = 'first';
        setOffset(offset + PAGE_SIZE);
      } else if (direction === -1 && offset > 0) {
        pendingSelect.current = 'last';
        setOffset(Math.max(0, offset - PAGE_SIZE));
      }
      return undefined;
    },
    [selectedIndex, rows, offset, total, open],
  );
  // A new page was asked for by moving on: open its first (or last) product.
  useEffect(() => {
    const want = pendingSelect.current;
    if (!want || rows.length === 0 || mappings.loading) return;
    pendingSelect.current = null;
    const pick = want === 'first' ? (rows.find(eligible) ?? rows[0]) : rows[rows.length - 1];
    if (pick) open(pick.sku, 'replace');
  }, [rows, mappings.loading, eligible, open]);

  function openFromQueue(row: ErpMappingRow) {
    deskPushed.current = !skuParam;
    setFinished(false);
    change({ sku: row.sku, state: stateGroup }, 'push');
    if (narrow) focusAfter.current = { kind: 'desk' };
  }

  function closeDesk() {
    const sku = selectedSku;
    if (deskPushed.current) {
      deskPushed.current = false;
      change({}, 'back');
    } else {
      change({ sku: undefined }, 'replace');
    }
    if (sku) focusAfter.current = { kind: 'row', sku };
  }

  function skip() {
    if (!selected) return;
    setSkipped((current) => new Set(current).add(selected.sku));
    advance(selected.sku, true);
  }

  // A conflict names the other products by SKU: show one of them.
  const showProduct = useCallback(
    (sku: string) => {
      change({ q: sku, state: 'all', sku }, skuParam ? 'replace' : 'push');
      setSearch(sku);
    },
    [change, skuParam],
  );
  // A pair the review could not offer is looked at on its own.
  const reviewProduct = useCallback(
    (sku: string) => {
      change({ q: '', state: 'all', sku }, 'push');
      setSearch('');
    },
    [change],
  );

  const latestRecent = recent.items[0];
  const canUndo = Boolean(latestRecent && undoablePhases.includes(latestRecent.phase));
  const undoLatest = useCallback(() => {
    if (latestRecent) void recent.undo(latestRecent.key);
  }, [latestRecent, recent]);

  if (linked.length === 0) {
    return (
      <section className="match-workspace" aria-label="Product matching" data-testid="erp-mapping">
        <QuietEmpty>Link a store to this connection to match its products.</QuietEmpty>
      </section>
    );
  }

  const attribute = mappings.data?.match_attribute ?? target?.website.erp.match_attribute ?? '';
  const withStore = severalStores(linked);
  const name = target ? websiteLabel(target.store, target.website, withStore) : 'The store';
  const words = target ? storeCopyFor(target.store.platform) : projectWords;
  const everything = Object.values(counts).reduce((sum, n) => sum + n, 0);
  const suggestions = mappings.data?.suggestions;
  const save = mappings.data?.save ?? { available: false };
  const saveAvailable = Boolean(save.available);
  const cannot = canManage ? cannotSaveText(save, words) : null;
  const reviewable = suggestions?.with_code ?? 0;
  const needMatch = mappings.data ? (counts.no_code ?? 0) + (counts.unmatched ?? 0) : undefined;

  function choose(next: Filter) {
    setFinished(false);
    change({ state: next === '' ? 'all' : next, sku: undefined }, 'replace');
  }

  // Counts are shown once they are known: a zero before the answer arrives
  // would say there is nothing to do.
  const known = mappings.data !== undefined && mappings.data !== null;
  const options = [
    ...matchFilterOrder.map((value) => ({
      value: value as Filter,
      label: statusFor('matchState', value).label,
      count: known ? (counts[value] ?? 0) : undefined,
    })),
    { value: '' as Filter, label: 'All', count: known ? everything : undefined },
  ];
  const workspaceVisible = !narrow || deskOpen;
  const number = selected ? offset + selectedIndex + 1 : 0;
  const nextRow = selected ? rows.slice(selectedIndex + 1).find(eligible) : undefined;

  const recentStrip = <RecentMatches items={recent.items} busy={false} onUndo={(key) => void recent.undo(key)} />;
  const emptyView = !mappings.loading && rows.length === 0;
  const phrase = emptyTitle(target?.store.catalog_products === 0, filter, query);

  return (
    <section className="match-workspace" aria-label="Product matching" data-testid="erp-mapping" data-step={narrow ? (deskOpen ? 'product' : 'queue') : undefined}>
      <div className="match-toolbar" data-testid="match-toolbar">
        <div className="match-toolbar__find">
          {linked.length > 1 ? (
            <div className="field match-toolbar__site">
              <label htmlFor="erp-mapping-store">{linked.some((entry) => entry.website.code !== '') ? 'Website' : 'Store'}</label>
              <Select
                id="erp-mapping-store"
                value={targetKey}
                onChange={(event) => {
                  setFinished(false);
                  change({ store: event.target.value, sku: undefined }, 'replace');
                }}
              >
                {linked.map((entry) => (
                  <option key={entry.key} value={entry.key}>
                    {websiteLabel(entry.store, entry.website, withStore)}
                  </option>
                ))}
              </Select>
            </div>
          ) : null}
          <div className="field match-toolbar__search">
            <label htmlFor="erp-mapping-search">Search products</label>
            <Input
              id="erp-mapping-search"
              name="product-search"
              type="search"
              value={search}
              placeholder="Name, SKU or ERP code…"
              autoComplete="off"
              spellCheck={false}
              onChange={(event) => setSearch(event.target.value)}
            />
          </div>
        </div>
        <div className="match-toolbar__chips">
          <FilterChips label="Show products" options={options} value={filter} onChange={choose} />
        </div>
        <div className="match-toolbar__actions" role="group" aria-label="Match many products">
          {canManage ? (
            <button
              type="button"
              className="button button--secondary button--small"
              disabled={!saveAvailable || reviewable === 0}
              title={!saveAvailable ? 'This store cannot save matches yet' : reviewable === 0 ? 'No product has a SKU that is an ERP article’s code or barcode' : undefined}
              onClick={() => setDialog('review')}
              data-testid="review-matches"
            >
              {reviewable > 0 ? `Review ${formatNumber(reviewable)} suggested` : 'Review suggested'}
            </button>
          ) : null}
          {canManage ? (
            <button type="button" className="button button--secondary button--small" disabled={!saveAvailable} title={!saveAvailable ? 'This store cannot save matches yet' : undefined} onClick={() => setDialog('import')} data-testid="import-matches">
              Import matches
            </button>
          ) : null}
          <a className="button button--secondary button--small" href={erpApi.matchExampleUrl(connection.id, { format: 'xlsx' })} download data-testid="download-example">
            Download example
          </a>
          <button type="button" className="button button--secondary button--small" onClick={() => setDialog('export')} data-testid="export-matches">
            Export products
          </button>
        </div>
      </div>

      {attribute ? null : (
        <Callout tone="warning" testId="erp-no-match-field">
          {name} has not chosen which {words.matchField} holds the ERP article code, so no product is matched. Choose it in {words.admin}: {words.settingsPath}.
        </Callout>
      )}
      {cannot && attribute ? (
        <Callout
          tone={cannot.update ? 'warning' : 'info'}
          title={cannot.title}
          testId="match-cannot-save"
          actions={
            cannot.update && onOpenStores ? (
              <button type="button" className="button button--secondary button--small" onClick={onOpenStores}>
                Open the Stores tab
              </button>
            ) : undefined
          }
        >
          {cannot.body}
        </Callout>
      ) : null}
      {skuMissing ? (
        <Callout tone="info" testId="match-sku-missing">
          No product with the SKU “{skuParam}” is in this list, so the first one is shown. Choose All to look across every group.
        </Callout>
      ) : null}
      <ErrorNotice error={mappings.error} />

      {mappings.loading && rows.length === 0 ? (
        <div className="match match--loading" aria-busy="true">
          <div className="match__queue">
            <LoadingRows label="Loading products" rows={7} rowHeight="56px" />
          </div>
          <div className="match__work">
            <div className="match-work">
              <div className="match-side">
                <LoadingRows label="Loading the product" rows={3} rowHeight="72px" />
              </div>
              <div className="match-candidates">
                <LoadingRows label="Loading the candidates" rows={3} rowHeight="96px" />
              </div>
            </div>
          </div>
        </div>
      ) : emptyView ? (
        <QuietEmpty testId="match-empty">
          {phrase}
          {target?.store.catalog_products === 0 ? `. ${emptyDescription(words.adminShort)}` : ''}
        </QuietEmpty>
      ) : (
        <div ref={setWorkEl} className={`match${deskOpen ? ' match--desk-open' : ''}`} data-testid="match-layout">
          <div className="match__queue">
            {attribute ? (
              <div className="match-queue__head" data-testid="erp-match-field">
                <span>
                  Match field <code>{attribute}</code>
                </span>
                <HelpTip label="How matching works">
                  <p>
                    A product matches the ERP article whose code is in its {words.matchField} value, and nothing else. Matched products take stock, prices and product data from the ERP; the others take
                    nothing.
                  </p>
                </HelpTip>
              </div>
            ) : null}
            <MatchQueue rows={rows} selectedSku={selectedSku} skipped={skipped} onSelect={openFromQueue} />
            <Pager
              className="match-queue__paging"
              first={offset + 1}
              last={offset + rows.length}
              total={total}
              onPrevious={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
              onNext={() => setOffset(offset + PAGE_SIZE)}
            />
          </div>
          <div className="match__work">
            {finished ? (
              <div className="match-done" role="status" data-testid="match-queue-done">
                <h3>You have been through this list</h3>
                <button type="button" className="button button--secondary" onClick={() => setFinished(false)}>
                  Back to the products
                </button>
                {recentStrip}
              </div>
            ) : selected && target ? (
              <MatchWorkspace
                key={`${targetKey}:${selected.sku}`}
                connection={connection}
                target={target}
                row={selected}
                attribute={attribute}
                pricesIncludeTax={mappings.data?.prices_include_tax ?? null}
                words={words}
                canManage={canManage}
                save={save}
                place={{ number, total, hasPrev: number > 1, hasNext: number < total }}
                nextSku={nextRow?.sku}
                skipped={skipped.has(selected.sku)}
                shortcutsOn={dialog === null && workspaceVisible}
                onSaved={recent.add}
                onAdvance={() => advance(selected.sku, false)}
                onSkip={skip}
                onStep={step}
                onUndo={undoLatest}
                canUndo={canUndo}
                onChanged={reload}
                onShowProduct={showProduct}
                onBack={closeDesk}
                headingRef={deskHeading}
                status={recentStrip}
              />
            ) : null}
          </div>
        </div>
      )}

      {target ? (
        <>
          <ReviewMatchesDialog
            open={dialog === 'review'}
            connection={connection}
            target={target}
            withStore={withStore}
            onClose={() => setDialog(null)}
            onDone={reload}
            onReviewProduct={reviewProduct}
          />
          <ExportMatchesDialog open={dialog === 'export'} connectionId={connection.id} target={target} withStore={withStore} needMatch={needMatch} total={mappings.data ? everything : undefined} onClose={() => setDialog(null)} />
        </>
      ) : null}
      <ImportMatchesDialog open={dialog === 'import'} connection={connection} linked={linked} initialKey={targetKey} withStore={withStore} onClose={() => setDialog(null)} onDone={reload} />
    </section>
  );
}

function emptyTitle(noCatalog: boolean, filter: Filter, query: string): string {
  if (noCatalog) return 'The store has not sent its product list';
  if (query) return 'No product matches your search';
  if (filter === 'no_code') return 'Every product has an ERP code';
  return 'No products here';
}

/** How a store that never sent its product list is made to: the one empty state that needs an instruction. */
function emptyDescription(admin: string): string {
  return `The extension sends it daily and when it is set up; in ${admin} you can also send it from the extension settings`;
}
